/**
 * Toast event bus. Decouples call sites (lib/* and pages/*) from the React
 * tree, so any code -- including non-component utilities -- can call
 * notify.success(...) without prop drilling or context plumbing.
 *
 * The Toaster component (src/components/Toaster.tsx) subscribes to this bus
 * and renders the current queue. Mount <Toaster /> once near the root.
 *
 * Design notes:
 *   - Auto-dismiss after 3s for success/info, 6s for error (user might be
 *     reading the error text). Override with the `durationMs` option.
 *   - Toasts stack bottom-right, newest on top.
 *   - Pure module-level pub/sub: no React context, no provider component.
 *
 * Sized for what we need today; revisit if we hit limitations.
 */

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  title?: string;
  expiresAt: number; // epoch ms
}

type Listener = (toasts: Toast[]) => void;

let _next = 1;
let _queue: Toast[] = [];
const _listeners = new Set<Listener>();
const _timers = new Map<number, ReturnType<typeof setTimeout>>();

function _emit() {
  for (const l of _listeners) l([..._queue]);
}

function _enqueue(
  kind: ToastKind,
  message: string,
  opts?: { title?: string; durationMs?: number }
) {
  const id = _next++;
  const durationMs =
    opts?.durationMs ?? (kind === 'error' ? 6000 : 3000);
  const toast: Toast = {
    id,
    kind,
    message,
    title: opts?.title,
    expiresAt: Date.now() + durationMs,
  };
  _queue = [..._queue, toast];
  _emit();
  const t = setTimeout(() => dismissToastById(id), durationMs);
  _timers.set(id, t);
  return id;
}

/** Subscribe to the toast queue. Returns an unsubscribe function. */
export function subscribeToToasts(listener: Listener): () => void {
  _listeners.add(listener);
  listener([..._queue]);
  return () => {
    _listeners.delete(listener);
  };
}

/** Imperatively dismiss a specific toast by id (e.g. from the X button). */
export function dismissToastById(id: number) {
  const t = _timers.get(id);
  if (t) clearTimeout(t);
  _timers.delete(id);
  const before = _queue.length;
  _queue = _queue.filter((q) => q.id !== id);
  if (_queue.length !== before) _emit();
}

/** Public API. */
export const notify = {
  success(message: string, opts?: { title?: string; durationMs?: number }) {
    return _enqueue('success', message, opts);
  },
  error(message: string, opts?: { title?: string; durationMs?: number }) {
    return _enqueue('error', message, opts);
  },
  info(message: string, opts?: { title?: string; durationMs?: number }) {
    return _enqueue('info', message, opts);
  },
};
